/**
 * EPUB text extraction for the audiobook reader.
 *
 * An EPUB is a ZIP of XHTML documents plus a manifest, which makes it a much
 * better source for narration than a PDF. A PDF stores positioned glyphs, so
 * extracted text arrives with hard line breaks mid-sentence and hyphenated word
 * splits, and you can hear those seams in the speech. An EPUB gives real
 * paragraphs, so the sentence splitter downstream gets clean input.
 *
 * Returns chapters in SPINE order (the author's reading order), not filename
 * order, which is often different.
 */

const JSZIP_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';

/** Load JSZip once, from the CDN, mirroring how pdf.js is loaded. */
function loadJsZip() {
  if (window.JSZip) return Promise.resolve(window.JSZip);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = JSZIP_SRC;
    s.onload = () => (window.JSZip ? resolve(window.JSZip) : reject(new Error('Could not load EPUB reader')));
    s.onerror = () => reject(new Error('Could not load EPUB reader'));
    document.head.appendChild(s);
  });
}

/** Namespace-proof element lookup: EPUBs use dc:, opf:, or no prefix at all. */
function byLocalName(root, name) {
  const out = [];
  const walk = (el) => {
    for (const child of el.children || []) {
      if (child.localName === name) out.push(child);
      walk(child);
    }
  };
  walk(root);
  return out;
}

/** Resolve an href against the OPF's own directory, collapsing ../ segments. */
function resolvePath(baseDir, href) {
  const clean = decodeURIComponent(String(href).split('#')[0]);
  const parts = (baseDir + clean).split('/');
  const stack = [];
  for (const p of parts) {
    if (!p || p === '.') continue;
    if (p === '..') stack.pop();
    else stack.push(p);
  }
  return stack.join('/');
}

/**
 * XHTML to readable text. Drops non-prose elements outright, then uses block
 * boundaries as paragraph breaks so the sentence splitter does not run two
 * unrelated lines together (headings into body text, for instance).
 */
function htmlToText(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script, style, nav, svg, head, sup.calibre, .calibre_hidden').forEach((n) => n.remove());
  const BLOCK = 'p, div, br, li, h1, h2, h3, h4, h5, h6, tr, blockquote, section';
  doc.querySelectorAll(BLOCK).forEach((n) => n.append('\n'));
  return (doc.body?.textContent || '')
    .replace(/ /g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** First heading in a chapter, used as its display name. */
function chapterTitle(html, fallback) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const h = doc.querySelector('h1, h2, h3, title');
  const t = h?.textContent?.trim().replace(/\s+/g, ' ');
  return t && t.length <= 90 ? t : fallback;
}

/**
 * Extract an EPUB into { title, author, chapters: [{ title, text }] }.
 * Chapters with almost no prose (covers, blank pages) are skipped so the
 * narration does not open by reading a filename.
 */
export async function extractEpub(file) {
  const JSZip = await loadJsZip();
  const zip = await JSZip.loadAsync(await file.arrayBuffer());

  // 1. META-INF/container.xml points at the OPF package document.
  const containerFile = zip.file('META-INF/container.xml');
  if (!containerFile) throw new Error('That does not look like a valid EPUB (no container.xml).');
  const containerXml = await containerFile.async('string');
  const container = new DOMParser().parseFromString(containerXml, 'application/xml');
  const rootfile = container.querySelector('rootfile');
  const opfPath = rootfile?.getAttribute('full-path');
  if (!opfPath) throw new Error('That EPUB has no package document.');

  const opfDir = opfPath.includes('/') ? opfPath.slice(0, opfPath.lastIndexOf('/') + 1) : '';
  const opfXml = await zip.file(opfPath).async('string');
  const opf = new DOMParser().parseFromString(opfXml, 'application/xml');

  const title = byLocalName(opf, 'title')[0]?.textContent?.trim() || file.name.replace(/\.epub$/i, '');
  const author = byLocalName(opf, 'creator')[0]?.textContent?.trim() || '';

  // 2. manifest: id -> { href, type }
  const manifest = {};
  for (const item of byLocalName(opf, 'item')) {
    manifest[item.getAttribute('id')] = {
      href: item.getAttribute('href'),
      type: item.getAttribute('media-type') || '',
    };
  }

  // 3. spine gives the reading order.
  const spine = byLocalName(opf, 'itemref').map((r) => r.getAttribute('idref'));
  if (!spine.length) throw new Error('That EPUB has no reading order.');

  const chapters = [];
  for (const idref of spine) {
    const item = manifest[idref];
    if (!item || !/html/i.test(item.type)) continue;
    const entry = zip.file(resolvePath(opfDir, item.href));
    if (!entry) continue;

    const html = await entry.async('string');
    const text = htmlToText(html);
    // Skip covers, blank pages and title pages: too little prose to narrate.
    if (text.length < 200) continue;
    chapters.push({ title: chapterTitle(html, `Chapter ${chapters.length + 1}`), text });
  }

  if (!chapters.length) throw new Error('No readable text found in that EPUB.');
  return { title, author, chapters };
}

export default extractEpub;
